import { DateTime } from 'luxon';
import { TimeValue } from './TimeValue';

/**
 * Calculate the next scheduled time for an automation
 *
 * @param {object} params - Parameters for calculation
 * @param {string} params.scheduleType - HOURLY, DAILY, WEEKLY, MONTHLY
 * @param {number} params.scheduleInterval - Interval value (e.g., every X hours/days)
 * @param {string|TimeValue} params.scheduleTime - Time of day to run (e.g., "13:30:00")
 * @param {number|string} params.scheduleDay - Day of month for MONTHLY, or day of week for WEEKLY
 * @param {Array<string>} params.scheduleDays - Days of week for WEEKLY schedule
 * @param {string} params.timezone - User's timezone (e.g., "America/New_York")
 * @returns {string} ISO date string of next scheduled time in UTC
 */
export function calculateNextScheduledAt({
  scheduleType,
  scheduleInterval = 1,
  scheduleTime,
  scheduleDay,
  scheduleDays = [],
  timezone = 'UTC'
}) {
  // Handle TimeValue objects
  let timeString = scheduleTime;
  if (scheduleTime instanceof TimeValue) {
    // Always use the local time value for calculations
    timeString = scheduleTime.format === 'UTC' 
      ? scheduleTime.toLocal().value 
      : scheduleTime.value;
  }
  
  console.log(`🕒 Calculating next run with time: ${timeString} in timezone ${timezone}`);
  
  // Convert time string to minutes since midnight for easier calculations
  const timeParts = typeof timeString === 'string' 
    ? timeString.split(':') 
    : ['9', '0', '0'];
    
  const hours = parseInt(timeParts[0], 10) || 0;
  const minutes = parseInt(timeParts[1], 10) || 0;
  const seconds = parseInt(timeParts[2], 10) || 0;
  
  // Start with current date/time in the user's timezone
  let now = DateTime.now().setZone(timezone);
  let nextRun = now.set({ second: 0, millisecond: 0 });
  
  // Calculate based on schedule type
  switch (scheduleType) {
    case 'HOURLY':
      // Set to the next occurrence based on interval
      if (nextRun.minute > minutes) {
        // If we've passed the target minutes, move to next hour
        nextRun = nextRun.plus({ hours: 1 }).set({ minute: minutes, second: seconds });
      } else {
        // Otherwise, set to target minutes in current hour
        nextRun = nextRun.set({ minute: minutes, second: seconds });
      }
      
      // Apply hourly interval (handle case where interval > 1)
      const currentHour = nextRun.hour;
      const adjustedHour = Math.ceil(currentHour / scheduleInterval) * scheduleInterval;
      if (adjustedHour > currentHour) {
        nextRun = nextRun.set({ hour: adjustedHour });
      } else {
        nextRun = nextRun.plus({ hours: scheduleInterval }).set({ hour: (currentHour + scheduleInterval) % 24 });
      }
      break;
      
    case 'DAILY':
      // Set time components
      nextRun = nextRun.set({ hour: hours, minute: minutes, second: seconds });
      
      // If that time today has already passed, move to tomorrow
      if (nextRun <= now) {
        nextRun = nextRun.plus({ days: 1 });
      }
      
      // Apply daily interval
      if (scheduleInterval > 1) {
        const daysToAdd = scheduleInterval - 1;
        nextRun = nextRun.plus({ days: daysToAdd });
      }
      break;
      
    case 'WEEKLY': {
      // Set time components
      nextRun = nextRun.set({ hour: hours, minute: minutes, second: seconds });
      
      // If no days specified, default to Monday
      const daysOfWeek = scheduleDays.length > 0 ? scheduleDays : ['Monday'];
      
      // Convert day names to numbers (1 = Monday, 7 = Sunday in Luxon)
      const dayNumbers = daysOfWeek.map(day => {
        const dayMap = {
          'Monday': 1, 'Tuesday': 2, 'Wednesday': 3, 'Thursday': 4,
          'Friday': 5, 'Saturday': 6, 'Sunday': 7
        };
        return dayMap[day] || 1;
      });
      
      // Sort day numbers for proper calculation
      dayNumbers.sort((a, b) => a - b);
      
      // Get current day of week (1-7)
      const currentDayOfWeek = nextRun.weekday;
      
      // Find the next day of week to run
      let nextDayOfWeek = dayNumbers.find(day => day > currentDayOfWeek);
      
      if (!nextDayOfWeek) {
        // If no day found, wrap to first day of next week
        nextDayOfWeek = dayNumbers[0];
        nextRun = nextRun.plus({ days: (7 - currentDayOfWeek) + nextDayOfWeek });
      } else {
        // Move to the next found day
        nextRun = nextRun.plus({ days: nextDayOfWeek - currentDayOfWeek });
      }
      
      // If the calculated time is in the past (meaning it's the same day but the time has passed)
      if (nextRun <= now && nextDayOfWeek === currentDayOfWeek) {
        // Move to the next occurrence of the day
        const nextOccurrence = dayNumbers.find(day => day > nextDayOfWeek);
        if (!nextOccurrence) {
          // If no next day found, wrap to first day of next week
          nextRun = nextRun.plus({ days: (7 - nextDayOfWeek) + dayNumbers[0] });
        } else {
          // Move to the next found day
          nextRun = nextRun.plus({ days: nextOccurrence - nextDayOfWeek });
        }
      }
      
      // Apply weekly interval if needed
      if (scheduleInterval > 1) {
        nextRun = nextRun.plus({ weeks: scheduleInterval - 1 });
      }
      break;
    }
      
    case 'MONTHLY': {
      // Parse scheduleDay - default to 1st of month if invalid
      const targetDay = parseInt(scheduleDay, 10) || 1;
      const safeDay = Math.min(targetDay, 28); // Ensure day exists in every month
      
      // Set time components and target day
      nextRun = nextRun.set({ hour: hours, minute: minutes, second: seconds, day: safeDay });
      
      // If that time this month has already passed, move to next month
      if (nextRun <= now) {
        nextRun = nextRun.plus({ months: 1 }).set({ day: safeDay });
      }
      
      // Apply monthly interval
      if (scheduleInterval > 1) {
        nextRun = nextRun.plus({ months: scheduleInterval - 1 });
      }
      
      // Handle special case for days 29-31
      if (targetDay > 28) {
        // Try to set the actual target day
        const daysInMonth = nextRun.daysInMonth;
        if (targetDay <= daysInMonth) {
          nextRun = nextRun.set({ day: targetDay });
        } else {
          // If target day doesn't exist in this month, use last day of month
          nextRun = nextRun.set({ day: daysInMonth });
        }
      }
      break;
    }
      
    default:
      console.warn(`Unknown schedule type: ${scheduleType}, defaulting to DAILY`);
      // Set to same time tomorrow
      nextRun = nextRun.plus({ days: 1 }).set({ hour: hours, minute: minutes, second: seconds });
  }
  
  // Convert the local time to UTC for API storage
  const utcDateTime = nextRun.toUTC();
  const isoString = utcDateTime.toISO();
  
  console.log(`🕒 Next scheduled time calculated: ${isoString} (UTC)`);
  return isoString;
}